// @dx context
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetIdentity } from "@refinedev/core";
import { supabaseClient } from "../utility/supabaseClient";
import { googleClient } from "@services/google/withGoogleAuth";
import { Sentry } from "../sentry";
import type { Employee } from "@shared/types/employee";
import {
  CurrentEmployeeProvider,
  type CurrentEmployeeValue,
} from "@ds/actions/CurrentEmployeeContext";
import { EmployeeContext, REQUIRED_GOOGLE_SCOPES, type EmployeeContextValue } from "./useCurrentEmployee";
// drive scope covers drive.readonly and drive.file
function scopeCovers(userScope: string, required: string): boolean {
  if (userScope === required) return true;
  if (required.endsWith('.readonly') || required.endsWith('.file')) {
    const base = required.replace(/\.(readonly|file)$/, '');
    if (userScope === base) return true;
  }
  return false;
}

export const EmployeeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: identity, isLoading: identityLoading } = useGetIdentity<{
    email: string;
  }>();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [rpcLoading, setRpcLoading] = useState(true);
  const [googleScopesOk, setGoogleScopesOk] = useState<boolean | null>(null);
  // Liveness of the stored Google token. The scope check below reads only the
  // stored user_google_tokens.scope string, which still looks complete after
  // the refresh token is revoked Google-side — so a confirmed-dead probe
  // result is held here and re-applied on DB-only re-checks (window focus);
  // without it the banner would flash off-then-on on every focus while the
  // token is dead. Cleared only by a passing probe.
  const probeFailedRef = useRef(false);
  // Bumped on every check run — an in-flight probe from a superseded run must
  // not overwrite the newer run's state (e.g. set true after tokens vanished).
  const probeGenRef = useRef(0);

  const checkGoogleScopes = useCallback(async () => {
    const gen = ++probeGenRef.current;
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      if (!user) { setGoogleScopesOk(false); return; }

      const { data: tokenRow } = await supabaseClient
        .from("user_google_tokens")
        .select("scope")
        .eq("user_id", user.id)
        .maybeSingle<{ scope: string | null }>();

      if (!tokenRow?.scope) {
        setGoogleScopesOk(false);
        return;
      }

      const userScopes: string[] = tokenRow.scope.split(' ');
      const ok = REQUIRED_GOOGLE_SCOPES.every(required =>
        userScopes.some(us => scopeCovers(us, required))
      );
      setGoogleScopesOk(ok && !probeFailedRef.current);
      if (!ok) return;

      // Token liveness probe — deliberately NOT awaited: the login gate waits
      // for this function, and the probe costs an edge-fn roundtrip plus a
      // possible OAuth refresh. A dead token flips the banner in late instead.
      // Only a CONFIRMED requiresReauth flips it — tokenHealth never throws
      // and reports transport/unknown failures as requiresReauth:false, so a
      // network blip can't nag the operator into re-authorizing.
      void googleClient
        .tokenHealth(REQUIRED_GOOGLE_SCOPES)
        .then((health) => {
          if (gen !== probeGenRef.current) return; // superseded by a newer check
          if (health.ok) {
            probeFailedRef.current = false;
            setGoogleScopesOk(true);
          } else if (health.requiresReauth) {
            probeFailedRef.current = true;
            setGoogleScopesOk(false);
          }
        });
    } catch {
      setGoogleScopesOk(false);
    }
  }, []);

  useEffect(() => {
    if (identityLoading || !identity?.email) return;

    setRpcLoading(true);
    (async () => {
      // The scope check must SETTLE before the loading gate opens: EmployeeGate
      // holds the whole shell on `isLoading`, and GoogleScopeBanner renders
      // above the routed content. If the check resolves after the gate opens,
      // the banner mounts into an already-interactive page and reflows the
      // entire layout — shifting anchored popups mid-click. Run it in parallel
      // with the employee RPC (it never throws — it catches internally) and
      // release the gate only when both are done.
      const scopesSettled = checkGoogleScopes();
      try {
        const rpcResult = await supabaseClient.rpc("get_my_employee");
        // The client is created without a Database generic, so the RPC payload
        // is untyped — `get_my_employee` returns 0..1 employee rows.
        const data = rpcResult.data as Employee[] | null;
        const error = rpcResult.error;
        if (error) {
          setEmployee(null);
          Sentry.captureException(error);
          Sentry.setUser(null);
        } else {
          setEmployee(data?.[0] ?? null);
          if (data?.[0]) {
            Sentry.setUser({ email: identity.email, id: String(data[0].id) });
            Sentry.setTag("employee.roles", (data[0].roles ?? []).join(","));
          } else {
            Sentry.setUser(null);
          }
        }
      } catch (err) {
        setEmployee(null);
        Sentry.captureException(err);
        Sentry.setUser(null);
      } finally {
        await scopesSettled;
        setRpcLoading(false);
      }
    })();
  }, [identity?.email, identityLoading, checkGoogleScopes]);

  // Re-check Google scopes on window focus (e.g. after returning from OAuth popup)
  useEffect(() => {
    const handleFocus = () => {
      if (googleScopesOk === false) checkGoogleScopes();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [googleScopesOk, checkGoogleScopes]);

  const isLoading = identityLoading || rpcLoading;

  const value = useMemo<EmployeeContextValue>(
    () => ({
      employee,
      roles: employee?.roles ?? [],
      isLoading,
      hasRole: (...roles) =>
        employee?.roles?.some((r) => roles.includes(r)) ?? false,
      googleScopesOk,
      recheckGoogleScopes: checkGoogleScopes,
    }),
    [employee, isLoading, googleScopesOk, checkGoogleScopes]
  );

  const dsValue = useMemo<CurrentEmployeeValue>(
    () => ({
      employee,
      roles: employee?.roles ?? [],
      hasRole: (...roles) =>
        employee?.roles?.some((r) => roles.includes(r)) ?? false,
    }),
    [employee]
  );

  return (
    <EmployeeContext.Provider value={value}>
      <CurrentEmployeeProvider value={dsValue}>
        {children}
      </CurrentEmployeeProvider>
    </EmployeeContext.Provider>
  );
};